import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Settings, 
  Eye, 
  Save, 
  Trash2, 
  Clock, 
  Target, 
  Shuffle,
  CheckCircle,
  AlertCircle,
  HelpCircle,
  Loader2,
  GripVertical,
  Edit3,
  Copy,
  BarChart3
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Switch } from '../../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Badge } from '../../ui/badge';
import { Separator } from '../../ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { ScrollArea } from '../../ui/scroll-area';
import { useToast } from '../../../hooks/useToast';
import { useTenantContext } from '../../../context/TenantContext';
import { 
  createQuiz, 
  updateQuiz, 
  getQuizByLessonId, 
  deleteQuiz,
  type Quiz,
  type QuizQuestion,
  validateQuizData
} from '../../../services/elearningQuizService';
import { QuestionManager } from './QuestionManager';
import { QuizPreview } from './QuizPreview';
import { QuizStatistics } from './QuizStatistics';

interface QuizBuilderProps {
  lessonId: string;
  isOpen: boolean;
  onClose: () => void;
  onSave?: (quiz: Quiz) => void;
}

const QuizBuilder: React.FC<QuizBuilderProps> = ({ lessonId, isOpen, onClose, onSave }) => {
  const { tenant } = useTenantContext();
  const { toast } = useToast();

  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [errors, setErrors] = useState<string[]>([]);

  const [quizForm, setQuizForm] = useState({
    title: '',
    description: '',
    instructions: '',
    passing_score: 70,
    time_limit: null as number | null,
    max_attempts: null as number | null,
    is_randomized: false,
    show_correct_answers: true,
    show_results_immediately: true,
    allow_review: true,
    is_active: true
  });

  useEffect(() => {
    if (isOpen && lessonId && tenant?.id) {
      loadQuiz();
    }
  }, [isOpen, lessonId, tenant?.id]);

  const loadQuiz = async () => {
    if (!tenant?.id) return;
    
    setIsLoading(true);
    try {
      const existingQuiz = await getQuizByLessonId(lessonId, tenant.id);
      
      if (existingQuiz) {
        setQuiz(existingQuiz);
        setQuizForm({
          title: existingQuiz.title,
          description: existingQuiz.description || '',
          instructions: existingQuiz.instructions || '',
          passing_score: existingQuiz.passing_score,
          time_limit: existingQuiz.time_limit,
          max_attempts: existingQuiz.max_attempts,
          is_randomized: existingQuiz.is_randomized,
          show_correct_answers: existingQuiz.show_correct_answers,
          show_results_immediately: existingQuiz.show_results_immediately,
          allow_review: existingQuiz.allow_review,
          is_active: existingQuiz.is_active
        });
      } else {
        // Nouveau quiz
        setQuiz(null);
        setQuizForm({
          title: '',
          description: '',
          instructions: '',
          passing_score: 70,
          time_limit: null,
          max_attempts: null,
          is_randomized: false,
          show_correct_answers: true,
          show_results_immediately: true,
          allow_review: true,
          is_active: true
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement du quiz:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger le quiz',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!tenant?.id) return;

    const validationErrors = validateQuizData({ ...quizForm, lesson_id: lessonId });
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSaving(true);
    try {
      let savedQuiz: Quiz;
      
      if (quiz) {
        // Mise à jour
        savedQuiz = await updateQuiz(quiz.id, quizForm, tenant.id);
        toast({
          title: 'Quiz mis à jour',
          description: 'Le quiz a été mis à jour avec succès',
        });
      } else {
        // Création
        savedQuiz = await createQuiz({ ...quizForm, lesson_id: lessonId }, tenant.id);
        toast({
          title: 'Quiz créé',
          description: 'Le quiz a été créé avec succès',
        });
      }

      setQuiz(savedQuiz);
      onSave?.(savedQuiz);
      setErrors([]);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de sauvegarder le quiz',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!quiz || !tenant?.id) return;

    const confirmed = window.confirm('Êtes-vous sûr de vouloir supprimer ce quiz ? Cette action est irréversible.');
    if (!confirmed) return;

    try {
      await deleteQuiz(quiz.id, tenant.id);
      toast({
        title: 'Quiz supprimé',
        description: 'Le quiz a été supprimé avec succès',
      });
      setQuiz(null);
      onClose();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer le quiz',
        variant: 'destructive'
      });
    }
  };

  const handleFormChange = (field: string, value: any) => {
    setQuizForm(prev => ({ ...prev, [field]: value }));
    setErrors([]);
  };

  const formatTime = (minutes: number | null) => {
    if (!minutes) return 'Aucune limite';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl h-[90vh] bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border-gray-700">
        <DialogHeader className="border-b border-gray-700 pb-4">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl font-bold text-white flex items-center">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                <HelpCircle className="w-5 h-5 text-white" />
              </div>
              {quiz ? 'Modifier le Quiz' : 'Créer un Quiz'}
            </DialogTitle>
            
            <div className="flex items-center space-x-2">
              {quiz && (
                <Badge variant="outline" className="border-green-500 text-green-400">
                  {quiz.questions?.length || 0} question{(quiz.questions?.length || 0) !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          </div>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center h-96">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full"
            />
            <span className="ml-3 text-gray-400">Chargement du quiz...</span>
          </div>
        ) : (
          <div className="flex-1 overflow-hidden">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
              <TabsList className="grid w-full grid-cols-4 bg-gray-800 border-gray-700 text-white">
                <TabsTrigger 
                  value="settings" 
                  className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Paramètres
                </TabsTrigger>
                <TabsTrigger 
                  value="questions" 
                  className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                  disabled={!quiz}
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  Questions
                </TabsTrigger>
                <TabsTrigger 
                  value="preview" 
                  className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                  disabled={!quiz}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Aperçu
                </TabsTrigger>
                <TabsTrigger 
                  value="statistics" 
                  className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                  disabled={!quiz}
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Statistiques
                </TabsTrigger>
              </TabsList>

              <div className="flex-1 overflow-hidden">
                <TabsContent value="settings" className="h-full">
                  <ScrollArea className="h-full pr-4">
                    <div className="space-y-6 p-6">
                      {/* Erreurs */}
                      <AnimatePresence>
                        {errors.length > 0 && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="bg-red-500/10 border border-red-500/20 rounded-lg p-4"
                          >
                            <div className="flex items-center mb-2">
                              <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
                              <span className="text-red-400 font-medium">Erreurs de validation</span>
                            </div>
                            <ul className="text-red-300 text-sm space-y-1">
                              {errors.map((error, index) => (
                                <li key={index}>• {error}</li>
                              ))}
                            </ul>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Informations générales */}
                      <Card className="bg-gray-800/50 border-gray-700">
                        <CardHeader>
                          <CardTitle className="text-white flex items-center">
                            <Edit3 className="w-5 h-5 mr-2 text-blue-400" />
                            Informations générales
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <Label htmlFor="title" className="text-gray-300">Titre du quiz *</Label>
                            <Input
                              id="title"
                              value={quizForm.title}
                              onChange={(e) => handleFormChange('title', e.target.value)}
                              placeholder="Ex: Quiz de validation du module"
                              className="bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                            />
                          </div>

                          <div>
                            <Label htmlFor="description" className="text-gray-300">Description</Label>
                            <Textarea
                              id="description"
                              value={quizForm.description}
                              onChange={(e) => handleFormChange('description', e.target.value)}
                              placeholder="Description courte du quiz..."
                              className="bg-gray-700 border-gray-600 text-white placeholder-gray-400 resize-none"
                              rows={3}
                            />
                          </div>

                          <div>
                            <Label htmlFor="instructions" className="text-gray-300">Instructions</Label>
                            <Textarea
                              id="instructions"
                              value={quizForm.instructions}
                              onChange={(e) => handleFormChange('instructions', e.target.value)}
                              placeholder="Instructions détaillées pour les étudiants..."
                              className="bg-gray-700 border-gray-600 text-white placeholder-gray-400 resize-none"
                              rows={4}
                            />
                          </div>
                        </CardContent>
                      </Card>

                      {/* Paramètres de scoring */}
                      <Card className="bg-gray-800/50 border-gray-700">
                        <CardHeader>
                          <CardTitle className="text-white flex items-center">
                            <Target className="w-5 h-5 mr-2 text-green-400" />
                            Paramètres de scoring
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <Label htmlFor="passing_score" className="text-gray-300">
                              Score de passage (%)
                            </Label>
                            <div className="flex items-center space-x-4">
                              <Input
                                id="passing_score"
                                type="number"
                                min="0"
                                max="100"
                                value={quizForm.passing_score}
                                onChange={(e) => handleFormChange('passing_score', parseInt(e.target.value) || 0)}
                                className="bg-gray-700 border-gray-600 text-white w-24"
                              />
                              <div className="flex-1 bg-gray-700 rounded-full h-2">
                                <div 
                                  className="bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${quizForm.passing_score}%` }}
                                />
                              </div>
                              <Badge variant="outline" className="border-green-500 text-green-400">
                                {quizForm.passing_score}%
                              </Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Paramètres de temps et tentatives */}
                      <Card className="bg-gray-800/50 border-gray-700">
                        <CardHeader>
                          <CardTitle className="text-white flex items-center">
                            <Clock className="w-5 h-5 mr-2 text-orange-400" />
                            Temps et tentatives
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <Label htmlFor="time_limit" className="text-gray-300">
                              Limite de temps (minutes)
                            </Label>
                            <div className="flex items-center space-x-4">
                              <Input
                                id="time_limit"
                                type="number"
                                min="1"
                                value={quizForm.time_limit || ''}
                                onChange={(e) => handleFormChange('time_limit', e.target.value ? parseInt(e.target.value) : null)}
                                placeholder="Aucune limite"
                                className="bg-gray-700 border-gray-600 text-white"
                              />
                              <Badge variant="outline" className="border-orange-500 text-orange-400">
                                {formatTime(quizForm.time_limit)}
                              </Badge>
                            </div>
                          </div>

                          <div>
                            <Label htmlFor="max_attempts" className="text-gray-300">
                              Nombre maximum de tentatives
                            </Label>
                            <div className="flex items-center space-x-4">
                              <Input
                                id="max_attempts"
                                type="number"
                                min="1"
                                value={quizForm.max_attempts || ''}
                                onChange={(e) => handleFormChange('max_attempts', e.target.value ? parseInt(e.target.value) : null)}
                                placeholder="Illimité"
                                className="bg-gray-700 border-gray-600 text-white"
                              />
                              <Badge variant="outline" className="border-purple-500 text-purple-400">
                                {quizForm.max_attempts || 'Illimité'}
                              </Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Options d'affichage */}
                      <Card className="bg-gray-800/50 border-gray-700">
                        <CardHeader>
                          <CardTitle className="text-white flex items-center">
                            <Eye className="w-5 h-5 mr-2 text-cyan-400" />
                            Options d'affichage
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <Label className="text-gray-300">Mélanger les questions</Label>
                              <p className="text-sm text-gray-400">Afficher les questions dans un ordre aléatoire</p>
                            </div>
                            <Switch
                              checked={quizForm.is_randomized}
                              onCheckedChange={(checked) => handleFormChange('is_randomized', checked)}
                              className="data-[state=checked]:bg-yellow-500 data-[state=checked]:text-blue-600 data-[state=unchecked]:bg-gray-700 data-[state=unchecked]:text-yellow-500"
                            />
                          </div>

                          <Separator className="bg-gray-700" />

                          <div className="flex items-center justify-between">
                            <div>
                              <Label className="text-gray-300">Afficher les bonnes réponses</Label>
                              <p className="text-sm text-gray-400">Montrer les corrections après le quiz</p>
                            </div>
                            <Switch
                              checked={quizForm.show_correct_answers}
                              onCheckedChange={(checked) => handleFormChange('show_correct_answers', checked)}
                              className="data-[state=checked]:bg-yellow-500 data-[state=checked]:text-blue-600 data-[state=unchecked]:bg-gray-700 data-[state=unchecked]:text-yellow-500"
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <div>
                              <Label className="text-gray-300">Résultats immédiats</Label>
                              <p className="text-sm text-gray-400">Afficher le score dès la fin du quiz</p>
                            </div>
                            <Switch
                              checked={quizForm.show_results_immediately}
                              onCheckedChange={(checked) => handleFormChange('show_results_immediately', checked)}
                              className="data-[state=checked]:bg-yellow-500 data-[state=checked]:text-blue-600 data-[state=unchecked]:bg-gray-700 data-[state=unchecked]:text-yellow-500"
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <div>
                              <Label className="text-gray-300">Permettre la révision</Label>
                              <p className="text-sm text-gray-400">Autoriser la consultation des réponses</p>
                            </div>
                            <Switch
                              checked={quizForm.allow_review}
                              onCheckedChange={(checked) => handleFormChange('allow_review', checked)}
                              className="data-[state=checked]:bg-yellow-500 data-[state=checked]:text-blue-600 data-[state=unchecked]:bg-gray-700 data-[state=unchecked]:text-yellow-500"
                            />
                          </div>

                          <Separator className="bg-gray-700" />

                          <div className="flex items-center justify-between">
                            <div>
                              <Label className="text-gray-300">Quiz actif</Label>
                              <p className="text-sm text-gray-400">Rendre le quiz disponible aux étudiants</p>
                            </div>
                            <Switch
                              checked={quizForm.is_active}
                              onCheckedChange={(checked) => handleFormChange('is_active', checked)}
                              className="data-[state=checked]:bg-yellow-500 data-[state=checked]:text-blue-600 data-[state=unchecked]:bg-gray-700 data-[state=unchecked]:text-yellow-500"
                            />
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="questions" className="h-full">
                  {quiz && (
                    <QuestionManager 
                      quiz={quiz} 
                      onQuizUpdate={(updatedQuiz) => setQuiz(updatedQuiz)}
                    />
                  )}
                </TabsContent>

                <TabsContent value="preview" className="h-full">
                  {quiz && <QuizPreview quiz={quiz} />}
                </TabsContent>

                <TabsContent value="statistics" className="h-full">
                  {quiz && <QuizStatistics quizId={quiz.id} />}
                </TabsContent>
              </div>
            </Tabs>
          </div>
        )}

        <DialogFooter className="border-t border-gray-700 pt-4">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-2">
              {quiz && (
                <Button
                  variant="outline"
                  onClick={handleDelete}
                  className="border-red-500 text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                onClick={onClose}
                className="border-gray-600 text-gray-400 hover:bg-gray-700"
              >
                Annuler
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving}
                className="bg-gradient-to-r from-blue-500 to-purple-600 text-white/70 hover:text-white hover:from-blue-600 hover:to-purple-700"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sauvegarde...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {quiz ? 'Mettre à jour' : 'Créer le quiz'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { QuizBuilder };
